import React, { useState, type ReactElement } from "react";

import { Box, Flex, Icon, Text } from "@chakra-ui/react";
import { useSelector } from "react-redux";

import AddIcon from "@/assets/svg/add-list-icon.svg";
import DefaultModal from "@/components/ui/modals/DefaultModal";
import { useActions } from "@/hooks/useActions";
import { useAddCompaniesToListMutation, useLazyGetAllListQuery } from "@/services/newList.service";
import newCompaniesListSelectors from "@/store/newCompaniesList/newCompaniesList.selectors";
import newListSelectors from "@/store/newLists/newLists.slectors";

import AddModal from "../../shared/AddModal";

import ModalListItem from "./ModalListItem";

/**
 * Props for AddToModal component.
 * 
 * @interface AddToModalProps
 * @property {Object} addToControl - Control object for modal state.
 * @property {() => void} addToControl.onClose - Callback function to close the modal.
 * @property {boolean} addToControl.isOpen - Flag to indicate if the modal is open.
 * @see {@link src/components/ui/modals/DefaultModal.tsx}
 */
interface AddToModalProps {
  addToControl: {
    onClose: () => void;
    isOpen: boolean;
  };
}

/**
 * Interface for representing a list item.
 * 
 * @interface ListItem
 * @property {string} id - Unique identifier for the list.
 * @property {string} title - Title of the list.
 * @property {any[]} [entities] - Array of entities in the list.
 * @see {@link src/store/newLists/newLists.slectors.ts}
 */
interface ListItem {
  id: string;
  title: string;
  entities?: any[];
}

/**
 * Component for adding companies to a list.
 * Renders a modal allowing the user to add selected companies to an existing list.
 *
 * @param {AddToModalProps} props - The props for the component.
 * @returns {React.ReactElement} The rendered AddToModal component.
 * @see {@link src/services/newList.service.ts}
 * @see {@link src/components/companies/companiesList/newCompaniesList/viewListTools/list/addtoListModal/ModalListItem.jsx}
 */
const AddToModal = ({ addToControl }: AddToModalProps): ReactElement => {
  const { clearSelectedCompanies } = useActions();
  const lists = useSelector(newListSelectors.getAllLists());
  const [getAllList] = useLazyGetAllListQuery();
  const [isAddlist, setIsAddList] = useState(false);
  const selectedCompanies = useSelector(newCompaniesListSelectors.getSelectedCompanies());
  
  const [addCompaniesToList] = useAddCompaniesToListMutation();
  
  // Map selected companies to their IDs with proper type annotation.
  const selectedCompaniesIds = selectedCompanies?.map((item: ListItem) => item.id);
  
  /**
   * Opens the modal to add a new list.
   */
  const onOpenAddList = (): void => {
    setIsAddList(true);
  };

  /**
   * Closes the modal and resets the add list state.
   */
  const onCloseHandler = (): void => {
    addToControl.onClose();
    setIsAddList(false);
  };

  /**
   * Cancels the add list mode.
   */
  const onCancelHandler = (): void => {
    setIsAddList(false);
  };

  /**
   * Adds companies to the specified list.
   *
   * @param {string} listId - Identifier of the list to add companies to.
   */
  const onAddCompaniesToList = (listId: string): void => {
    const options = {
      listId,
      entityIds: selectedCompaniesIds,
    };

    addCompaniesToList(options).then(
      () => {
        onCloseHandler();
        clearSelectedCompanies();
        getAllList({});
      }
    );
  };

  return (
    <DefaultModal
      {...{
        isOpen: addToControl?.isOpen,
        onClose: onCloseHandler,
        isCloseBtn: false,
        content_style: {
          p: "12px",
        },
        modalStyle: {
          borderRadius: "4px",
        },
      }}
    >
      <>
        {!isAddlist ? (
          <Flex
            direction={"column"}
            w={"212px"}
            rowGap={"4px"}
            fontSize={"14px"}
            fontWeight={"400"}
            lineHeight={"1"}
            letterSpacing={"2%"}
          >
            <Flex
              align={"center"}
              columnGap={"12px"}
              h={"32px"}
              cursor={"pointer"}
              onClick={onOpenAddList}
              px={"10px"}
            >
              <Icon as={AddIcon} w={"16px"} h={"16px"} />
              <Text>Add List</Text>
            </Flex>
            <Box w={"100%"} h={"1px"} bg={"lightGray"} />
            <Flex w={"100%"} direction={"column"} rowGap={"8px"}>
              {lists?.map((item: ListItem) => (
                <ModalListItem
                  key={item.id}
                  item={item}
                  handler={onAddCompaniesToList}
                />
              ))}
            </Flex>
          </Flex>
        ) : (
          <Flex p={"12px"}>
            <AddModal
              isView={false}
              title={"Add List"}
              onClose={onCancelHandler}
            />
          </Flex>
        )}
      </>
    </DefaultModal>
  );
};

export default AddToModal;
